import { b2bSideCopy } from '../../b2bSideCopy';
import { boardStatusLabel } from '../offeringFormat';
import type { MsaasJobListingResponse, OfferingGeography } from '../types';

const geographyLabel = (geography: OfferingGeography) => {
  const place =
    geography.preferenceType === 'ZIP'
      ? geography.zipCode
      : [geography.city, geography.stateCode].filter(Boolean).join(', ');
  return `${place || 'Seller location'} · within ${geography.radiusMiles} mi`;
};

const rosterLine = (member: NonNullable<MsaasJobListingResponse['rosterSummary']>[number]) =>
  [
    member.providerCategory,
    member.specialtyName,
    boardStatusLabel(member.boardStatus),
    member.yearsExperience != null ? `${member.yearsExperience} yrs experience` : null,
  ]
    .filter(Boolean)
    .join(' · ');

const Heading = ({ children }: { children: string }) => (
  <h3 className="mb-2 text-xl font-bold leading-snug text-content-primary">{children}</h3>
);

/** Where the seller can work: licensed or covered states, and any in-person coverage points. */
export const OfferingCoverage = ({ listing }: { listing: MsaasJobListingResponse }) => {
  const geographies = listing.geographies ?? [];
  return (
    <section className="mb-4 text-left" data-cy="offering-coverage">
      <Heading>Coverage</Heading>
      <dl className="space-y-2 text-[1rem] leading-6">
        <div>
          <dt className="text-sm font-semibold leading-5 text-content-secondary">
            License and coverage states
          </dt>
          <dd>{listing.stateCodes.length > 0 ? listing.stateCodes.join(', ') : '—'}</dd>
        </div>
        {geographies.length > 0 && (
          <div>
            <dt className="text-sm font-semibold leading-5 text-content-secondary">
              In-person coverage
            </dt>
            <dd>
              <ul>
                {geographies.map((geography, index) => (
                  <li key={`${geography.sourceZipGeographyId ?? index}`}>
                    {geographyLabel(geography)}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
};

/** The seller's providers as a buyer may see them: no names. */
export const OfferingRoster = ({ listing }: { listing: MsaasJobListingResponse }) => {
  const roster = listing.rosterSummary ?? [];
  return (
    <section className="mb-4 text-left" data-cy="offering-roster">
      <Heading>
        {roster.length > 0 ? `Roster (${listing.rosterCount ?? roster.length})` : 'Roster'}
      </Heading>
      {roster.length === 0 ? (
        <p className="text-[1rem] leading-6 text-content-secondary">
          {b2bSideCopy('OFFERING').emptyRosterNote}
        </p>
      ) : (
        <ul className="text-[1rem] leading-6">
          {roster.map((member, index) => (
            <li key={index} className="py-0.5">
              {rosterLine(member) || 'Provider'}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
